import {
  Box,
  HStack,
  IconButton,
  Link,
  Separator,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Pencil, Trash2 } from 'lucide-react'
import React from 'react'

import { createTranslator, type TranslateFn } from '@mangovoice/mango-ui'

export interface PbxAssociationCardData {
  pbxId: number
  pbxName: string
  userId: number
  userName: string
  sendingFaxEmail: string | null | undefined
  sendingFaxNumber: string | null | undefined
  pin: number | null | undefined
  faxTitle: string | null | undefined
  assignedExtensionCount?: number
  assignedDeviceCount?: number
}

export interface PbxAssociationCardProps {
  association: PbxAssociationCardData
  canEdit?: boolean
  canDelete?: boolean
  onEdit?: () => void
  onViewPbx?: (pbxId: number) => void
  onViewAssignedExtensions?: (pbxId: number) => void
  onRemove: (
    pbxId: number,
    userId: number,
    userName: string,
    assignedExtensionCount: number,
    assignedDeviceCount: number,
  ) => void
  t?: TranslateFn
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <VStack align="stretch" gap={2}>
    <HStack justify="space-between" align="flex-start">
      <Text fontSize="xs" fontWeight="medium" color="fg.muted">
        {label}
      </Text>
      <Box maxW="70%" textAlign="right">
        {value}
      </Box>
    </HStack>
  </VStack>
)

export const PbxAssociationCard: React.FC<PbxAssociationCardProps> = ({
  association,
  canEdit = false,
  canDelete = false,
  onEdit,
  onViewPbx,
  onViewAssignedExtensions,
  onRemove,
  t,
}) => {
  const tr = React.useMemo(() => createTranslator(t), [t])
  return (
    <Box
      alignSelf="stretch"
      px={4}
      py={3}
      bg="bg.subtle"
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.muted"
    >
      <VStack align="stretch" gap={3}>
        <HStack justify="space-between" align="flex-start">
          {onViewPbx ? (
            <Link
              fontSize="sm"
              fontWeight="semibold"
              color="fg.info"
              textDecoration="underline"
              href="#"
              cursor="pointer"
              onClick={(event) => {
                event.preventDefault()
                onViewPbx(association.pbxId)
              }}
            >
              {association.pbxName}
            </Link>
          ) : (
            <Text fontSize="sm" fontWeight="semibold" color="fg.default">
              {association.pbxName}
            </Text>
          )}
          <HStack gap={2}>
            {canEdit && onEdit && (
              <IconButton
                size="xs"
                variant="subtle"
                colorPalette="brand"
                aria-label={tr(
                  'users.pbxAssociationCard.editAria',
                  `Edit association for ${association.pbxName}`,
                  { pbxName: association.pbxName },
                )}
                onClick={onEdit}
              >
                <Pencil size={14} />
              </IconButton>
            )}
            {canDelete && (
              <IconButton
                size="xs"
                variant="subtle"
                colorPalette="error"
                aria-label={tr(
                  'users.pbxAssociationCard.removeAria',
                  `Remove ${association.userName} from ${association.pbxName}`,
                  {
                    userName: association.userName,
                    pbxName: association.pbxName,
                  },
                )}
                onClick={() =>
                  onRemove(
                    association.pbxId,
                    association.userId,
                    association.userName,
                    association.assignedExtensionCount ?? 0,
                    association.assignedDeviceCount ?? 0,
                  )
                }
              >
                <Trash2 size={14} />
              </IconButton>
            )}
          </HStack>
        </HStack>

        <Row
          label={tr(
            'users.pbxAssociationCard.assignedExtensions',
            'Assigned Extensions',
          )}
          value={
            association.assignedExtensionCount == null ? (
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                —
              </Text>
            ) : onViewAssignedExtensions &&
              association.assignedExtensionCount > 0 ? (
              <Link
                fontSize="xs"
                fontWeight="medium"
                color="fg.info"
                textDecoration="underline"
                href="#"
                onClick={(event) => {
                  event.preventDefault()
                  onViewAssignedExtensions(association.pbxId)
                }}
              >
                {association.assignedExtensionCount}
              </Link>
            ) : (
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                {association.assignedExtensionCount}
              </Text>
            )
          }
        />
        <Separator />
        <Row
          label={tr('users.pbxAssociationCard.faxEmail', 'Fax Email')}
          value={
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              {association.sendingFaxEmail || '—'}
            </Text>
          }
        />
        <Separator />
        <Row
          label={tr('users.pbxAssociationCard.faxNumber', 'Fax Number')}
          value={
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              {association.sendingFaxNumber || '—'}
            </Text>
          }
        />
        <Separator />
        <Row
          label={tr('users.pbxAssociationCard.pin', 'PIN')}
          value={
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              {association.pin ?? '—'}
            </Text>
          }
        />
        <Separator />
        <Row
          label={tr('users.pbxAssociationCard.faxTitle', 'Fax Title')}
          value={
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              {association.faxTitle || '—'}
            </Text>
          }
        />
      </VStack>
    </Box>
  )
}
